import React, { useState, useEffect, useRef } from 'react';
import { Box, Chip, InputAdornment, Popover, Paper } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import InputUI from './InputUI';
import { isNil } from 'lodash';
import { sxConfig } from './interface.style';

const DebouncedInputUI = ({
  setQuickFilterSearchValue,
  quickFilterSearchValue,
  debounceMs = 1000,
  fieldName,
  mode = 'single',
  limitTags,
  ...props
}) => {
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');
  const [values, setValues] = useState(mode === 'multi' ? quickFilterSearchValue || [] : quickFilterSearchValue || '');
  const [isEditingChip, setIsEditingChip] = useState(false);

  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const popoverOpen = Boolean(popoverAnchorEl);

  useEffect(() => {
    if (mode === 'multi') {
      setValues(quickFilterSearchValue || []);
    } else {
      setInputValue(quickFilterSearchValue || '');
    }
  }, [quickFilterSearchValue, mode]);

  useEffect(() => {
    if (mode === 'multi') {
      if (!inputValue.trim()) return;
      const timeout = setTimeout(
        () => {
          const newVal = inputValue.trim();
          if (newVal && !values.includes(newVal)) {
            const newValues = [...values, newVal];
            setValues(newValues);
            if (fieldName) {
              setQuickFilterSearchValue(fieldName, newValues);
            } else {
              setQuickFilterSearchValue(newValues);
            }
            setIsEditingChip(false);
          }
          setInputValue('');
        },
        isEditingChip ? debounceMs * 2 : debounceMs
      );
      return () => clearTimeout(timeout);
    } else {
      const timeout = setTimeout(() => {
        if (!isNil(inputValue)) {
          if (fieldName) {
            setQuickFilterSearchValue(fieldName, inputValue);
          } else {
            setQuickFilterSearchValue(inputValue);
          }
        }
      }, debounceMs);
      return () => clearTimeout(timeout);
    }
  }, [inputValue, debounceMs, mode, isEditingChip]);

  const handleDelete = (valToRemove) => {
    const newVals = values.filter((v) => v !== valToRemove);
    setValues(newVals);
    if (fieldName) {
      setQuickFilterSearchValue(fieldName, newVals);
    } else {
      setQuickFilterSearchValue(newVals);
    }

    if (newVals.length <= limitTags) {
      setPopoverAnchorEl(null);
    }
  };

  return (
    <>
      <InputUI
        inputRef={inputRef}
        value={inputValue}
        variant="standard"
        onChange={(e) => setInputValue(e.target.value)}
        InputProps={{
          startAdornment:
            mode === 'multi' ? (
              <>
                {(values.length > limitTags ? values.slice(0, 4) : values).map((val, idx) => (
                  <Chip
                    key={idx}
                    label={val}
                    onClick={() => {
                      setInputValue(val);
                      handleDelete(val);
                      inputRef.current?.focus();
                      setIsEditingChip(true);
                    }}
                    sx={sxConfig.debouncedInputChip}
                    onDelete={() => handleDelete(val)}
                  />
                ))}
                {values.length > limitTags && (
                  <Chip label={`+${values.length - limitTags}`} onClick={(e) => setPopoverAnchorEl(e.currentTarget)} />
                )}
              </>
            ) : null,
          endAdornment: (
            <InputAdornment position="end" onClick={() => setInputValue('')}>
              {inputValue ? <ClearIcon color="primary" sx={sxConfig.debouncedInputendAdornment} /> : <Box width={24} />}
            </InputAdornment>
          ),
        }}
        InputLabelProps={{
          style: sxConfig.debouncedInputLabelColor,
          shrink: mode === 'multi' ? values.length > 0 || inputValue.length > 0 : undefined,
        }}
        {...props}
      />

      {mode === 'multi' && (
        <Popover
          open={popoverOpen}
          anchorEl={popoverAnchorEl}
          onClose={() => setPopoverAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <Paper sx={sxConfig.debouncedInputPaper}>
            <Box sx={sxConfig.debouncedInputPaperBox}>
              {values.map((val, idx) => (
                <Chip
                  key={idx}
                  label={val}
                  onDelete={() => handleDelete(val)}
                  onClick={() => {
                    setInputValue(val);
                    handleDelete(val);
                    setPopoverAnchorEl(null);
                  }}
                />
              ))}
            </Box>
            <InputUI
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              label="Új érték hozzáadása"
              fullWidth
              variant="standard"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" onClick={() => setInputValue('')}>
                    {inputValue ? (
                      <ClearIcon color="primary" sx={sxConfig.debouncedInputendAdornment} />
                    ) : (
                      <Box width={24} />
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Paper>
        </Popover>
      )}
    </>
  );
};

export default DebouncedInputUI;
